import { useState } from 'react';
import { Card, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreVertical,
  Calendar,
  FileText,
  ArrowUp,
  ArrowDown,
  Minus,
  Eye,
  Pencil,
  Copy,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TaskCardProps } from '@/types';
import { TaskAssigneePicker } from './task-assignee-picker';



export function TaskCard({
  id: _id,
  title,
  description,
  status,
  priority,
  dueDate,
  assigneeName,
  documents = [],
  isDragging,
  taskUuid,
  assignees = [],
  assigneeOptions = [],
  selectedAssigneeIds = [],
  onDragStart,
  onDragEnd,
  onClick,
  onEdit,
  onDelete,
  onDuplicate,
  onAssigneeToggle,
}: TaskCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const priorityInfo = {
    low: {
      icon: <ArrowDown className="h-3 w-3" />,
      color: 'text-blue-500',
      bg: 'bg-blue-50',
      label: 'Low',
    },
    medium: {
      icon: <Minus className="h-3 w-3" />,
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      label: 'Medium',
    },
    high: {
      icon: <ArrowUp className="h-3 w-3" />,
      color: 'text-rose-500',
      bg: 'bg-rose-50',
      label: 'High',
    },
  };

  const statusBorderClass = {
    done: 'border-l-emerald-500/50',
    doing: 'border-l-sky-500/50',
    todo: 'border-l-slate-300/50',
  };

  const taskKey = `T-${_id}`;
  const showClone = false;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group cursor-grab active:cursor-grabbing transition-all duration-500',
        isDragging && 'scale-95 opacity-50 rotate-3'
      )}
    >
      <Card
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border/40 bg-background/60 backdrop-blur-md shadow-[0_4px_12px_-4px_rgba(0,0,0,0.1)] transition-all duration-500',
          statusBorderClass[status],
          'border-l-[6px]',
          isHovered
            ? 'shadow-[0_20px_40px_-12px_rgba(0,0,0,0.15)] -translate-y-1 bg-background/80'
            : ''
        )}
        onClick={onClick}
      >
        {/* Glow effect on hover */}
        <div
          className={cn(
            'absolute -right-12 -top-12 h-24 w-24 rounded-full bg-primary/5 blur-2xl transition-all duration-500 opacity-0',
            isHovered && 'opacity-100 scale-150'
          )}
        />

        <CardHeader className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[9px] font-semibold tracking-tighter text-muted-foreground/50">
                  {taskKey}
                </span>
                <div className="h-0.5 w-0.5 rounded-full bg-muted-foreground/20" />
                <span className="text-[9px] font-medium text-muted-foreground/50 uppercase tracking-widest">
                  Task
                </span>
              </div>
              <h3 className="font-semibold text-xs leading-tight text-foreground/90 group-hover:text-primary transition-colors duration-300 line-clamp-2">
                {title}
              </h3>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-6 w-6 p-0 rounded-lg hover:bg-secondary/60 transition-all duration-300 z-10',
                    isHovered ? 'opacity-100' : 'opacity-30'
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-40 p-1 backdrop-blur-lg border-border/40 shadow-2xl"
              >
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick?.();
                  }}
                  className="rounded-lg px-2 py-1 text-xs font-medium gap-2"
                >
                  <Eye className="h-3.5 w-3.5 opacity-70" />
                  Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.();
                  }}
                  className="rounded-lg px-2 py-1 text-xs font-medium gap-2"
                >
                  <Pencil className="h-3.5 w-3.5 opacity-70" />
                  Edit
                </DropdownMenuItem>
                {showClone && <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate?.();
                  }}
                  className="rounded-lg px-2 py-1 text-xs font-medium gap-2"
                >
                  <Copy className="h-3.5 w-3.5 opacity-70" />
                  Clone
                </DropdownMenuItem>}
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.();
                  }}
                  className="text-destructive focus:text-destructive rounded-lg px-2 py-1 text-xs font-medium gap-2"
                >
                  <Trash2 className="h-3.5 w-3.5 opacity-70" />
                  Remove
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {description && (
            <div className="text-[10px] font-medium text-muted-foreground leading-snug line-clamp-2 opacity-80">
              {description.trim().startsWith('{"root"') ? (
                <div className="opacity-80">
                  {(() => {
                    try {
                      const parsed = JSON.parse(description);
                      const firstTextNode =
                        parsed.root.children[0]?.children?.[0]?.text;
                      return firstTextNode || 'Rich content...';
                    } catch {
                      return '';
                    }
                  })()}
                </div>
              ) : (
                description.replace(/<[^>]*>?/gm, '')
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-1 pt-1.5 border-t border-border/5">
            <div className="flex items-center gap-2">
              {priority && (
                <div
                  className={cn(
                    'flex items-center justify-center h-5 w-5 rounded-md transition-all duration-300',
                    priorityInfo[priority].bg,
                    priorityInfo[priority].color,
                    isHovered && 'scale-110 shadow-sm'
                  )}
                  title={`Priority: ${priorityInfo[priority].label}`}
                >
                  {priorityInfo[priority].icon}
                </div>
              )}

              {documents.length > 0 && (
                <div
                  className="flex items-center gap-1 text-[10px] font-semibold text-muted-foreground/60"
                  title={`${documents.length} Attachment(s)`}
                >
                  <FileText className="h-3.5 w-3.5 opacity-60" />
                  <span>{documents.length}</span>
                </div>
              )}

              {dueDate && (
                <div
                  className={cn(
                    'flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded transition-all duration-300',
                    new Date(dueDate) < new Date()
                      ? 'bg-rose-50 text-rose-500'
                      : 'bg-muted/30 text-muted-foreground/70'
                  )}
                >
                  <Calendar className="h-3.5 w-3.5 opacity-60" />
                  {new Date(dueDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center gap-1">
              <TaskAssigneePicker
                taskId={_id}
                taskUuid={taskUuid}
                assignees={assignees}
                assigneeOptions={assigneeOptions}
                selectedAssigneeIds={selectedAssigneeIds}
                fallbackAssigneeName={assigneeName}
                onAssigneeToggle={onAssigneeToggle}
              />
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  );
}
